import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

interface ViaCepResponse {
  erro?: boolean;
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
}

/**
 * Busca o endereço no ViaCEP quando o CEP tem 8 dígitos.
 * Retorna a dica exibida abaixo do campo.
 */
export function useCepLookup(
  cep: string | undefined,
  enabled: boolean,
  onAddress: (address: string) => void,
) {
  const [hint, setHint] = useState('');
  const callback = useRef(onAddress);
  callback.current = onAddress;
  const digits = (cep ?? '').replace(/\D/g, '');
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || digits.length !== 8) {
      setHint('');
      last.current = null;
      return;
    }
    if (last.current === digits) return;
    last.current = digits;

    let cancelled = false;
    let done = false;
    setHint('Buscando endereço...');

    axios
      .get<ViaCepResponse>(`https://viacep.com.br/ws/${digits}/json/`)
      .then(({ data }) => {
        done = true;
        if (cancelled) return;
        if (data.erro) {
          setHint('CEP não encontrado.');
          return;
        }
        callback.current(
          `${data.logradouro}, ${data.bairro}, ${data.localidade}/${data.uf}`,
        );
        setHint('Endereço preenchido pelo CEP.');
      })
      .catch(() => {
        done = true;
        if (!cancelled) setHint('');
      });

    return () => {
      cancelled = true;
      if (!done) last.current = null;
    };
  }, [digits, enabled]);

  return { hint };
}
